import { LoadingButton } from "@mui/lab";
import { Box, Grid, Button } from "@mui/material";
import { useState } from "react";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import uploads from "./uploads";
import MuiTabs from "../../components/MuiTabs";
import UploadIcon from "@mui/icons-material/Upload";
import {
  FormProvider,
  RHFFileField,
  RHFSelectField,
  RHFSearchableSelectField,
} from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { yupFileValidation } from "../../common/util";
import {
  useGetAllCourierPartnersCachedQuery,
  useGetAllActiveMerchantsIdAndNameCachedQuery,
} from "../../redux/commonApiCalls";
import { postMultipart } from "../../common/constants";
import { useSnackbar } from "notistack";
import _ from "lodash";
import PageTitle from "../../components/PageTitle";
import BuildIcon from "@mui/icons-material/Build";
import { useNavigate } from "react-router-dom";

// Upload Form Component that remounts when tab changes
function UploadForm({ currentUpload, courier, onSubmit, isSubmitting }) {
  const { resolver, defaultValues } = getDefaultValues(currentUpload);
  const navigate = useNavigate();

  const methods = useForm({ resolver, defaultValues });
  const { handleSubmit } = methods;

  // Get merchant data for merchant fields
  const { data: merchants = [] } =
    useGetAllActiveMerchantsIdAndNameCachedQuery();
  const activeMerchants = merchants.filter((val) => val.is_deleted === "0");
  const merchantOptions = _.orderBy(activeMerchants, "name").map((val) => ({
    value: val.id,
    label: val.companyName ? `${val.name} (${val.companyName})` : val.name,
  }));

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {currentUpload.fields.map((field, i) => (
            <Grid key={i} item xs={6}>
              {field.type === "merchant" && (
                <RHFSearchableSelectField
                  fullWidth
                  name={field.name}
                  label="Select Merchant"
                  options={merchantOptions}
                />
              )}
              {field.type === "courier" && (
                <RHFSearchableSelectField
                  fullWidth
                  label={`Select ${field.type}`}
                  options={courier.map((val) => ({
                    value: val.prozoId,
                    label: val.cpAccountCode + " ( " + val.prozoId + " )",
                  }))}
                  name={field.name}
                />
              )}
              {field.type === "select" && (
                <RHFSelectField
                  width="100%"
                  label={field.label}
                  options={field.options}
                  name={field.name}
                />
              )}
            </Grid>
          ))}
          <Grid item xs={12}>
            <RHFFileField name="file" />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              size="medium"
              startIcon={<UploadIcon />}
              type="submit">
              Upload
            </LoadingButton>
            <DownloadSampleFile fileName={currentUpload.sampleName} />

            {/* Add this button after line 90 - only show for Weight Zone Rule tab */}
            {currentUpload.value === 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<BuildIcon />}
                onClick={() => navigate("/uploads/build-rules")}>
                ProShip PACE
              </Button>
            )}
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
}

function getDefaultValues(currentUpload) {
  let defaultValues = {
    file: null,
  };
  let validationResolver = {
    file: yupFileValidation(Yup, "file"),
  };

  // Set default values and validation for each field in the current upload configuration
  for (let field of currentUpload.fields) {
    defaultValues[field.name] = "";
    validationResolver[field.name] =
      field.mandatory !== false
        ? Yup.string().required(`${field.name} is required`)
        : Yup.string();
  }

  return {
    resolver: yupResolver(Yup.object().shape(validationResolver)),
    defaultValues,
  };
}

export default function UploadFiles() {
  const andfilter = [{ field: "is_deleted", operator: "eq", value: "0" }];
  const filter = {
    andfilter: andfilter,
    offset: 0,
    limit: 1000,
    skipCount: true,
    includes: "id,name,cpAccountCode,prozoId,supportedShipmentType",
  };
  const { data: courier = [] } = useGetAllCourierPartnersCachedQuery(filter);
  const [active, setActive] = useState(0);
  const currentUpload = uploads.find((up) => up.value === active);
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    let formData = new FormData();
    formData.append("file", data?.file[0]);
    formData.append("type", currentUpload.type);
    if (currentUpload.value === 5) {
      formData.append("shipmentType", "B2B");
    }
    delete data.file;
    for (let k in data) {
      if (data[k] && data[k] !== "") {
        formData.append(k, data[k]);
      }
    }

    try {
      await postMultipart(currentUpload.endpoint, formData).then((res) => {
        if (res.status === 200) {
          enqueueSnackbar("Successfully uploaded!", { variant: "success" });
        } else if (res.data === "Rule Upload Successful.") {
          enqueueSnackbar("Rule Upload Successful", { variant: "success" });
        } else {
          enqueueSnackbar(`${res.data}`, { variant: "error" });
        }
      });
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(message, { variant: "error" });
    }
    setIsSubmitting(false);
  };
  return (
    <>
      <PageTitle>Uploads</PageTitle>
      <MuiTabs tabs={uploads} active={active} setActive={setActive} />
      <Box
        sx={{
          width: 1,
          boxShadow: 1,
          backgroundColor: "#fff",
          p: 2,
        }}>
        {currentUpload && (
          <UploadForm
            key={active}
            currentUpload={currentUpload}
            courier={courier}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </Box>
    </>
  );
}
